import { Theme } from "react-migration/lib/theme/Theme";
import clsx from "clsx";
import React, { FC, HTMLProps } from "react";
import { useTranslation } from "react-migration/lib/i18n/useTranslation";
import { MeasurementSystem } from "src/js/stores/user/store";
import { SupportedLocale } from "src/js/util/dates";
import { PriceTypes } from "react-migration/domains/comparables/typings/PriceTypes";
import { isZoopla } from "react-migration/domains/comparables/util/isZoopla";
import { isPerAreaMode } from "react-migration/domains/comparables/util/isPerAreaMode";
import { Translation } from "react-migration/lib/typings";
import { PriceModes } from "react-migration/domains/comparables/typings/PriceModes";
import { Currency, formatToCurrency } from "react-migration/lib/util/numberFormat";
import hasFeature from "src/js/stores/user/actions/hasFeature";
import Feature from "src/js/stores/user/Feature";
import { COMPARABLES_LIMIT, COMPARABLES_ZOOPLA_LIMIT } from "react-migration/constants";
import hasBetaFeature from "src/js/stores/user/actions/hasBetaFeature";
import { Tooltip } from "react-migration/components/Tooltip";
import { Spinner } from "react-migration/components/Spinner/Spinner";
import { DataPanel } from "react-migration/components/DataPanel";

export interface ResultsProps extends HTMLProps<HTMLDivElement> {
  areaSet: boolean;
  loading: boolean;
  totalResults: number;
  floorAreaResults: number;
  soldAverage: number;
  soldResults: number;
  marketAverage: number;
  marketResults: number;
  currency?: Currency;
  unitPreference: MeasurementSystem;
  locale: SupportedLocale;
  priceMode: PriceModes;
  priceType: PriceTypes;
}

interface ResultProps {
  currency?: Currency;
  priceMode: PriceModes;
  unitPreference: MeasurementSystem;
  amount: React.ReactNode;
  locale: SupportedLocale;
  results: number;
  theme?: Theme;
  loading: boolean;
}

function getAmount(amount: React.ReactNode, pricePerAreaFormatted: (amount: number) => string | undefined) {
  if (isNaN(Number(amount))) {
    return "-";
  }
  return typeof amount === "number" ? pricePerAreaFormatted(amount) : amount;
}

export const Result: FC<ResultProps> = ({ currency, locale, priceMode, unitPreference, amount, results, loading }) => {
  const { t } = useTranslation();
  const pricePerAreaFormatted = (amount: number) => {
    return formatToCurrency({
      currency,
      locale,
      amount,
      maximumFractionDigits: 0,
    });
  };
  return (
    <>
      <div className="flex gap-1 items-center">
        <div className="text-lg font-medium py-1">
          {loading ? <Spinner size="sm" /> : getAmount(amount, pricePerAreaFormatted)}
        </div>
        {isPerAreaMode(priceMode) && (
          <div className="text-xs text-text-base-tertiary">
            {t("comparables.calculator.results.per")}{" "}
            {unitPreference === MeasurementSystem.METRIC ? t("comparables.sqm.alt") : t("comparables.sqft.alt")}
          </div>
        )}
      </div>
      <div className="text-xs text-text-base-tertiary font-light">
        {t("comparables.calculator.results.messages.total", { total: results })}
        {results !== 1 && "s"}
      </div>
    </>
  );
};

export interface IMessageText {
  t: Translation;
  areaSet: boolean;
  loading: boolean;
  totalResults: number;
  floorAreaResults: number;
  soldAverage: number;
  marketAverage: number;
  priceType: PriceTypes;
  limit: number;
}
export const getMessageText = ({
  t,
  loading,
  areaSet,
  soldAverage,
  marketAverage,
  priceType,
  totalResults,
  floorAreaResults,
}: IMessageText): string => {
  if (loading) {
    return t("comparables.calculator.results.messages.calculating");
  } else if (!areaSet) {
    return t("comparables.calculator.results.messages.area");
  } else if (soldAverage || marketAverage) {
    if (priceType === PriceTypes.PER_AREA) {
      return t("comparables.calculator.results.messages.results.ppa", {
        total: totalResults,
        floor: floorAreaResults,
      });
    } else {
      return t("comparables.calculator.results.messages.results", {
        total: totalResults,
      });
    }
  } else {
    return t("comparables.calculator.results.messages.none");
  }
};

export const Message = ({
  loading,
  areaSet,
  soldAverage,
  marketAverage,
  priceType,
  totalResults,
  floorAreaResults,
  limit,
}: Omit<IMessageText, "t">) => {
  const { t } = useTranslation();
  return (
    <div className="text-xs px-2">
      {getMessageText({
        t,
        loading,
        areaSet,
        soldAverage,
        marketAverage,
        priceType,
        totalResults,
        floorAreaResults,
        limit,
      })}
      {totalResults >= limit && (
        <Tooltip.Wrapper>
          <Tooltip.Portal>
            <Tooltip.Content size="sm" side="top" align="center" theme={Theme.Light}>
              <Tooltip.Description>
                {t("comparables.calculator.results.messages.limit", { limit: limit })}
              </Tooltip.Description>
            </Tooltip.Content>
          </Tooltip.Portal>
          <Tooltip.Trigger>
            <i className="text-sm icon-lt-information-line ml-1" />
          </Tooltip.Trigger>
        </Tooltip.Wrapper>
      )}
    </div>
  );
};

export const Results: FC<ResultsProps> = ({
  areaSet,
  loading,
  totalResults,
  floorAreaResults,
  soldAverage,
  soldResults,
  marketAverage,
  marketResults,
  currency,
  unitPreference,
  locale,
  priceMode,
  priceType,
}) => {
  const { t } = useTranslation();
  const hometrackv2 = hasBetaFeature(Feature.hometrackv2) || hasFeature(Feature.zooplaFullAccess);

  return (
    <div data-testid="calc-results">
      <DataPanel.Grid columns={2} className="gap-1">
        <DataPanel.Cell
          title={
            // Gross but can remove with beta
            !hometrackv2
              ? isZoopla(priceMode)
                ? t("comparables.calculator.zoopla_results.last_value")
                : t("comparables.calculator.results.sold")
              : isZoopla(priceMode)
              ? t("comparables.filters.displayBy.zoopla_valuation_per_unit_last")
              : t("comparables.filters.displayBy.price")
          }
          className="bg-white"
        >
          <Result
            currency={currency}
            locale={locale}
            priceMode={priceMode}
            amount={soldAverage}
            loading={loading}
            unitPreference={unitPreference}
            results={soldResults}
          />
        </DataPanel.Cell>
        <DataPanel.Cell
          title={
            <>
              {!hometrackv2
                ? t("comparables.calculator.results.market")
                : isZoopla(priceMode)
                ? t("comparables.filters.displayBy.zoopla_valuation_current")
                : t("comparables.filters.displayBy.est_price")}
              {hasFeature(Feature.usAccess) ? null : (
                <Tooltip.Wrapper>
                  <Tooltip.Portal>
                    <Tooltip.Content size="sm" side="top" align="center" theme={Theme.Light}>
                      <Tooltip.Description>
                        {!hometrackv2
                          ? isZoopla(priceMode)
                            ? t("comparables.calculator.results.market.zoopla_tooltip")
                            : t("comparables.calculator.results.market.tooltip")
                          : isZoopla(priceMode)
                          ? t("comparables.filters.displayBy.zoopla_valuation_current.description")
                          : t("comparables.filters.displayBy.est_price.description")}
                      </Tooltip.Description>
                    </Tooltip.Content>
                  </Tooltip.Portal>
                  <Tooltip.Trigger>
                    <i className="text-sm icon-lt-information-line ml-1" />
                  </Tooltip.Trigger>
                </Tooltip.Wrapper>
              )}
            </>
          }
          className="bg-white"
        >
          <Result
            currency={currency}
            locale={locale}
            priceMode={priceMode}
            unitPreference={unitPreference}
            amount={marketAverage}
            results={marketResults}
            loading={loading}
          />
        </DataPanel.Cell>
      </DataPanel.Grid>
      <div className="mt-2 text-sm">
        <Message
          areaSet={areaSet}
          loading={loading}
          totalResults={totalResults}
          floorAreaResults={floorAreaResults}
          soldAverage={soldAverage}
          marketAverage={marketAverage}
          priceType={priceType}
          limit={isZoopla(priceMode) ? COMPARABLES_ZOOPLA_LIMIT : COMPARABLES_LIMIT}
        />
      </div>
    </div>
  );
};
